// src/components/ContractSchemaSelector.jsx
import React, { useMemo, useState } from "react";

// Central categorized list of contract schema options.
// NOTE:
// - Keep existing backend-hooked values exactly as-is where they already exist.
// - New values can be added now on the frontend and hooked up later as playbooks are created.
// - Auto-detect is intentionally preserved and rendered at the bottom.

const AUTO_DETECT_OPTION = {
  value: "",
  label: "Other / Not sure (auto-detect)",
};

const SCHEMA_GROUPS = [
  {
    category: "Real Estate & Property",
    options: [
      { value: "commercial_lease", label: "Commercial Lease Agreement" },
      { value: "commercial_property_purchase", label: "Commercial Property Purchase Agreement" },
      { value: "construction_agreement", label: "Construction Agreement" },
      { value: "land_sale_agreement", label: "Land Sale Agreement" },
      { value: "lease_assignment", label: "Lease Assignment Agreement" },
      { value: "property_management_agreement", label: "Property Management Agreement" },
      { value: "property_purchase_residential", label: "Property Purchase Agreement (Residential)" },
      { value: "real_estate_development_agreement", label: "Real Estate Development Agreement" },
      { value: "residential_lease", label: "Residential Lease Agreement" },
      { value: "sublease_agreement", label: "Sublease Agreement" },
    ],
  },
  {
    category: "Business Operations & Services",
    options: [
      { value: "consulting_agreement", label: "Consulting Agreement" },
      { value: "facilities_management_agreement", label: "Facilities Management Agreement" },
      { value: "freelancer_agreement", label: "Freelancer Agreement" },
      { value: "maintenance_agreement", label: "Maintenance Agreement" },
      { value: "master_services_agreement", label: "Master Services Agreement (MSA)" },
      { value: "outsourcing_agreement", label: "Outsourcing Agreement" },
      { value: "services_agreement", label: "Service Agreement / Contractor" },
      { value: "statement_of_work", label: "Statement of Work (SOW)" },
      { value: "supplier_agreement", label: "Supplier Agreement" },
      { value: "vendor_agreement", label: "Vendor Agreement" },
    ],
  },
  {
    category: "Business Structure & Ownership",
    options: [
      { value: "business_transfer_agreement", label: "Business Transfer Agreement" },
      { value: "buy_sell_agreement", label: "Buy-Sell Agreement" },
      { value: "corporate_bylaws", label: "Corporate Bylaws" },
      { value: "equity_vesting_agreement", label: "Equity Vesting Agreement" },
      { value: "founder_agreement", label: "Founder Agreement" },
      { value: "joint_venture_agreement", label: "Joint Venture Agreement" },
      { value: "llc_operating_agreement", label: "LLC Operating Agreement" },
      { value: "merger_agreement", label: "Merger Agreement" },
      { value: "partnership_agreement", label: "Partnership Agreement" },
      { value: "shareholder_agreement", label: "Shareholder Agreement" },
      { value: "stock_purchase_agreement", label: "Stock Purchase Agreement" },
    ],
  },
  {
    category: "Finance, Banking & Lending",
    options: [
      { value: "convertible_note_agreement", label: "Convertible Note Agreement" },
      { value: "credit_agreement", label: "Credit Agreement" },
      { value: "debt_settlement_agreement", label: "Debt Settlement Agreement" },
      { value: "loan_agreement", label: "Loan Agreement" },
      { value: "loan_modification_agreement", label: "Loan Modification Agreement" },
      { value: "mortgage_agreement", label: "Mortgage Agreement" },
      { value: "personal_guarantee_agreement", label: "Personal Guarantee Agreement" },
      { value: "promissory_note", label: "Promissory Note" },
      { value: "revenue_sharing_agreement", label: "Revenue Sharing Agreement" },
      { value: "security_agreement", label: "Security Agreement (Collateral)" },
    ],
  },
  {
    category: "Sales, Purchase & Distribution",
    options: [
      { value: "asset_purchase_agreement", label: "Asset Purchase Agreement" },
      { value: "business_purchase_agreement", label: "Business Purchase Agreement" },
      { value: "distribution_agreement", label: "Distribution Agreement" },
      { value: "equipment_purchase_agreement", label: "Equipment Purchase Agreement" },
      { value: "franchise_agreement", label: "Franchise Agreement" },
      { value: "licensing_agreement", label: "Licensing Agreement" },
      { value: "reseller_agreement", label: "Reseller Agreement" },
      { value: "supply_agreement", label: "Supply Agreement" },
      { value: "vehicle_lease", label: "Vehicle Lease (car / auto)" },
      { value: "vehicle_purchase_agreement", label: "Vehicle Purchase Agreement" },
    ],
  },
  {
    category: "Employment & Workforce",
    options: [
      { value: "commission_agreement", label: "Commission Agreement" },
      { value: "confidentiality_employee", label: "Employee Confidentiality Agreement" },
      { value: "employment_offer", label: "Employment Offer / Employment Agreement" },
      { value: "executive_employment_agreement", label: "Executive Employment Agreement" },
      { value: "independent_contractor_agreement", label: "Independent Contractor Agreement" },
      { value: "non_compete_agreement", label: "Non-Compete Agreement" },
      { value: "non_solicitation_agreement", label: "Non-Solicitation Agreement" },
      { value: "severance_agreement", label: "Severance Agreement" },
      { value: "work_for_hire_agreement", label: "Work-for-Hire Agreement" },
    ],
  },
  {
    category: "Legal Risk & Protection",
    options: [
      { value: "arbitration_agreement", label: "Arbitration Agreement" },
      { value: "dispute_resolution_agreement", label: "Dispute Resolution Agreement" },
      { value: "indemnity_agreement", label: "Indemnity Agreement" },
      { value: "limitation_of_liability_agreement", label: "Limitation of Liability Agreement" },
      { value: "nda", label: "NDA – Non-Disclosure Agreement" },
      { value: "release_waiver_agreement", label: "Release & Waiver Agreement" },
      { value: "settlement_agreement", label: "Settlement Agreement" },
    ],
  },
  {
    category: "Technology, Digital & Data",
    options: [
      { value: "api_agreement", label: "API Agreement" },
      { value: "cloud_services_agreement", label: "Cloud Services Agreement" },
      { value: "data_processing_agreement", label: "Data Processing Agreement (DPA)" },
      { value: "privacy_policy", label: "Privacy Policy" },
      { value: "saas_agreement", label: "SaaS Agreement" },
      { value: "software_development_agreement", label: "Software Development Agreement" },
      { value: "subscription_agreement", label: "Subscription Agreement" },
      { value: "subscription_membership", label: "Subscription / Membership Terms" },
      { value: "terms_of_service", label: "Terms of Service" },
    ],
  },
  {
    category: "Intellectual Property & Licensing",
    options: [
      { value: "copyright_license_agreement", label: "Copyright License Agreement" },
      { value: "intellectual_property_assignment", label: "Intellectual Property Assignment Agreement" },
      { value: "software_ip_license_agreement", label: "Software / IP License Agreement" },
      { value: "technology_transfer_agreement", label: "Technology Transfer Agreement" },
      { value: "trademark_license_agreement", label: "Trademark License Agreement" },
    ],
  },
  {
    category: "Personal & Family High-Stakes",
    options: [
      { value: "caregiver_agreement", label: "Caregiver Agreement" },
      { value: "co_ownership_agreement", label: "Co-Ownership Agreement" },
      { value: "postnuptial_agreement", label: "Postnuptial Agreement" },
      { value: "prenuptial_agreement", label: "Prenuptial Agreement" },
    ],
  },
];

function sortGroups(groups) {
  return groups.map((group) => ({
    ...group,
    options: [...group.options].sort((a, b) => a.label.localeCompare(b.label)),
  }));
}

export default function ContractSchemaSelector({
  schemaId,
  onSchemaChange,
  disabled = false,
  // 🔴 Optional inline error from parent (e.g., "Please select a contract type.")
  error,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedGroups = useMemo(() => sortGroups(SCHEMA_GROUPS), []);
  const lowerSearch = searchTerm.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!lowerSearch) return normalizedGroups;

    return normalizedGroups
      .map((group) => ({
        ...group,
        options: group.options.filter((opt) =>
          opt.label.toLowerCase().includes(lowerSearch)
        ),
      }))
      .filter((group) => group.options.length > 0);
  }, [lowerSearch, normalizedGroups]);

  const autoDetectMatches =
    !lowerSearch ||
    AUTO_DETECT_OPTION.label.toLowerCase().includes(lowerSearch);

  const hasVisibleResults =
    filteredGroups.length > 0 || autoDetectMatches;

  const groupsToRender =
    hasVisibleResults && filteredGroups.length > 0
      ? filteredGroups
      : normalizedGroups;

  const hasError = !!error;

  // 🧠 Placeholder:
  //   - We treat `schemaId === null` or `undefined` as "no selection yet"
  //   - Auto-detect STILL uses value === "" exactly as before
  const SELECT_PLACEHOLDER = "__placeholder__";
  const selectValue =
    schemaId === null || schemaId === undefined
      ? SELECT_PLACEHOLDER
      : schemaId;

  const handleChange = (e) => {
    const val = e.target.value;
    if (val === SELECT_PLACEHOLDER) {
      onSchemaChange?.(null);
    } else {
      onSchemaChange?.(val);
    }
  };

  return (
    <div style={styles.schemaWrap}>
      <label htmlFor="schema-select" style={styles.schemaLabel}>
        Contract type
      </label>

      {/* 🔍 Inline search box within the same component “box” */}
      <input
        type="text"
        placeholder="Search contract types"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled}
        style={styles.schemaSearch}
      />

      <select
        id="schema-select"
        value={selectValue}
        onChange={handleChange}
        disabled={disabled}
        style={{
          ...styles.schemaSelect,
          ...(hasError ? styles.schemaSelectError : null),
        }}
      >
        {/* 🔽 Default placeholder shown in the box until user makes a real choice */}
        <option value={SELECT_PLACEHOLDER} disabled hidden>
          Select your contract type
        </option>

        {groupsToRender.map((group) => (
          <optgroup key={group.category} label={group.category}>
            {group.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}

        {autoDetectMatches && (
          <optgroup label="Fallback">
            <option value={AUTO_DETECT_OPTION.value}>
              {AUTO_DETECT_OPTION.label}
            </option>
          </optgroup>
        )}
      </select>

      {/* Normal helper text */}
      <div style={styles.schemaHelp}>
        Selecting a type lets us verify your contract against the matching playbook. If you&apos;re
        not sure, pick <strong>Other / auto-detect</strong> and we&apos;ll classify it for you.
      </div>

      {/* 🔴 Inline error (short, red, under the select) */}
      {hasError && (
        <div style={styles.schemaErrorText}>
          {typeof error === "string" ? error : "Please select a contract type."}
        </div>
      )}
    </div>
  );
}

const styles = {
  schemaWrap: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 14,
    background: "#ffffff",
  },
  schemaLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 6,
  },
  schemaSearch: {
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontSize: 13,
    color: "#111827",
    marginBottom: 6,
    background: "#ffffff",
  },
  schemaSelect: {
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontSize: 14,
    color: "#111827",
    background: "#ffffff",
  },
  schemaSelectError: {
    borderColor: "#dc2626",
    boxShadow: "0 0 0 1px rgba(220,38,38,0.08)",
  },
  schemaHelp: {
    marginTop: 6,
    fontSize: 12,
    color: "#64748b",
    lineHeight: 1.4,
  },
  schemaErrorText: {
    marginTop: 4,
    fontSize: 12,
    color: "#b91c1c",
    fontWeight: 500,
  },
};
